import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { listTeamMembers, deleteTeamMember, updateTeamMember } from "../../../api/cms";
import SortableTableRow from "../../../components/SortableTableRow";

function byOrder(a, b) {
  if (a.order == null) return 1;
  if (b.order == null) return -1;
  return a.order - b.order;
}

export default function AdminTeamMembers() {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const load = () => {
    setLoading(true);
    listTeamMembers()
      .then(data => setTeamMembers([...data].sort(byOrder)))
      .catch(() => setError("Failed to load team members"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this team member?")) return;
    await deleteTeamMember(id);
    load();
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = teamMembers.findIndex(m => m.id === active.id);
    const newIndex = teamMembers.findIndex(m => m.id === over.id);
    const reordered = arrayMove(teamMembers, oldIndex, newIndex);
    setTeamMembers(reordered);
    setSaving(true);
    await Promise.all(reordered.map((m, i) => updateTeamMember(m.id, { ...m, order: i })));
    setSaving(false);
  };

  if (loading) return <p>Loading…</p>;
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Team members</h1>
          {saving && <span className="text-sm text-gray-400">Saving…</span>}
        </div>
        <Link to="/admin/team-members/new" className="bg-bahyellow rounded px-4 py-2 text-sm font-semibold hover:opacity-90">
          + Add team member
        </Link>
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-3 w-8" />
              <th className="px-4 py-3 font-semibold">Photo</th>
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Position</th>
              <th className="px-4 py-3 font-semibold">Teams</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={teamMembers.map(m => m.id)} strategy={verticalListSortingStrategy}>
              <tbody className="divide-y divide-gray-100">
                {teamMembers.map((m) => (
                  <SortableTableRow key={m.id} id={m.id}>
                    <td className="px-4 py-3">
                      {m.image && <img src={m.image} alt={m.name} className="w-10 h-10 rounded-full object-cover" />}
                    </td>
                    <td className="px-4 py-3 font-medium">{m.name}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{m.position}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{(m.teams || []).join(", ")}</td>
                    <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                      <Link to={`/admin/team-members/${m.id}/edit`} className="text-blue-600 hover:underline">Edit</Link>
                      <button onClick={() => handleDelete(m.id)} className="text-red-500 hover:underline">Delete</button>
                    </td>
                  </SortableTableRow>
                ))}
                {teamMembers.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No team members yet</td></tr>
                )}
              </tbody>
            </SortableContext>
          </DndContext>
        </table>
      </div>
    </div>
  );
}
